import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { defaultCategories, slug } from "../lib/categories";
import { categorize, learnRule, merchantKey, merchantName } from "../lib/categorize";
import { normStore, exactPairs } from "../lib/receiptOcr";
import { mergeSync, replaceSync, cleanCode, PHOTO_FORMAT } from "../lib/qrSync";
import { uid, setCurrency } from "../lib/format";
import { txKey, findTransferPairs, applyTransferPairs, billList, payoffSavings, rollForward, nextOccurrence, crossedMilestones, goalMilestones } from "../lib/finance";
import { today, daysBetween, addDays } from "../lib/format";
import { loadEnvelope, openDevice, openPasscode, persist, makeLock, wipeAll, saveReceipt, loadReceipt, deleteReceipt, reencryptReceipts, sweepReceipts, b64, unb64, deriveRaw, importRaw, openWithKey, sealJSON, openJSON } from "../lib/secureStore";
import { enableBio, unlockBio, disableBio } from "../lib/bio";
import { sampleData } from "../lib/sample";

const BUNDLE = "household-budget-receipts";

export const freshState = () => ({
  version: 2,
  setupDone: false,
  settings: { theme: "light", currency: "USD", inflation: 3, avgWindow: 3, lowBalance: 200, reminderDays: 3, reminderNotify: false, mileageRate: 0.7, autoLockMinutes: 5, lockOnHide: false },
  accounts: [],
  members: [{ id: "m1", name: "Household" }],
  categories: defaultCategories(),
  transactions: [],
  rules: [],
  statements: [],
  budget: { limits: {}, rollover: {}, manual: [], savedAt: null },
  bills: [],
  dismissedBills: [],
  billMoves: [],
  dismissedAlerts: [],
  debts: [],
  goals: [],
  alerts: [],
  dismissedReminders: [],
  mileage: [],
  tripTemplates: [],
  dismissedTripSuggestions: [],
  receiptAlertDismissed: [],
  vendorLinks: {},
  taxChecklist: {},
  receiptInbox: [],
  savedReceiptSearches: [],
  yearEndDismissed: null,
  irregular: [],
  audit: [],
});

function migrate(s) {
  const out = { ...freshState(), ...s };
  out.settings = { ...freshState().settings, ...(s.settings || {}) };
  if ((s.version || 1) < 2) {
    const ids = new Set(out.categories.map((c) => c.id));
    out.categories = [...out.categories, ...defaultCategories().filter((c) => c.id === "interest-charges" && !ids.has(c.id))];
    out.version = 2;
  }
  return out;
}

const Ctx = createContext(null);
export const useStore = () => useContext(Ctx);

const clone = (o) => (typeof structuredClone === "function" ? structuredClone(o) : JSON.parse(JSON.stringify(o)));

export function StoreProvider({ children }) {
  const [state, setState] = useState(null);
  const [status, setStatus] = useState("loading");
  const [savedAt, setSavedAt] = useState(null);
  const [undoCount, setUndoCount] = useState(0);
  const lock = useRef(null);
  const undoStack = useRef([]);
  const envelope = useRef(null);
  const stateRef = useRef(null);
  stateRef.current = state;

  useEffect(() => {
    (async () => {
      try {
        const rec = await loadEnvelope();
        envelope.current = rec;
        if (!rec) {
          setState(freshState());
          setStatus("ready");
        } else if (rec.mode === "passcode") setStatus("locked");
        else {
          setState(migrate(await openDevice(rec)));
          setStatus("ready");
        }
      } catch (e) {
        console.error(e);
        setState(freshState());
        setStatus("ready");
      }
    })();
  }, []);

  useEffect(() => {
    if (!state || status !== "ready") return;
    setCurrency(state.settings.currency);
    document.documentElement.classList.toggle("dark", state.settings.theme === "dark");
    const t = setTimeout(async () => {
      await persist(state, lock.current);
      setSavedAt(new Date());
    }, 350);
    return () => clearTimeout(t);
  }, [state, status]);

  // On open, drop receipt files no longer referenced by any transaction
  useEffect(() => {
    if (status === "ready" && stateRef.current) sweepReceipts(receiptIds()).catch(() => {});
  }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const flush = () => document.visibilityState === "hidden" && stateRef.current && status === "ready" && persist(stateRef.current, lock.current);
    document.addEventListener("visibilitychange", flush);
    return () => document.removeEventListener("visibilitychange", flush);
  }, [status]);

  const unlockWithBio = async () => {
    const key = await importRaw(await unlockBio());
    const s = await openWithKey(envelope.current, key);
    lock.current = { key, salt: envelope.current.salt };
    setState(migrate(s));
    setStatus("ready");
  };

  // Requires the current passcode so the same key can be wrapped by the biometric secret
  const turnOnBio = async (pass) => {
    await persist(stateRef.current, lock.current);
    const raw = await deriveRaw(pass, lock.current.salt);
    try {
      await openWithKey(await loadEnvelope(), await importRaw(raw));
    } catch {
      throw new Error("wrong-passcode");
    }
    await enableBio(raw);
  };

  const unlock = async (pass) => {
    const { state: s, key } = await openPasscode(envelope.current, pass);
    lock.current = { key, salt: envelope.current.salt };
    setState(migrate(s));
    setStatus("ready");
  };

  // Save, then drop all decrypted data and keys from memory
  const lockNow = async () => {
    if (!lock.current || status !== "ready") return;
    await persist(stateRef.current, lock.current);
    envelope.current = await loadEnvelope();
    lock.current = null;
    undoStack.current = [];
    setUndoCount(0);
    setState(null);
    setStatus("locked");
  };

  const update = useCallback((label, fn, auditEntries) => {
    const prev = stateRef.current;
    undoStack.current.push({ label, state: prev });
    if (undoStack.current.length > 30) undoStack.current.shift();
    const draft = clone(prev);
    fn(draft);
    if (auditEntries) {
      const entries = (typeof auditEntries === "function" ? auditEntries(prev, draft) : auditEntries).map((a) => ({ id: uid(), ts: new Date().toISOString(), ...a }));
      draft.audit = [...entries, ...(draft.audit || [])].slice(0, 1000);
    }
    stateRef.current = draft;
    setState(draft);
    setUndoCount(undoStack.current.length);
  }, []);

  const undo = () => {
    const last = undoStack.current.pop();
    if (!last) return null;
    const restored = { ...last.state, audit: [{ id: uid(), ts: new Date().toISOString(), action: "undo", summary: `Undid: ${last.label}` }, ...(stateRef.current?.audit || [])].slice(0, 1000) };
    stateRef.current = restored;
    setState(restored);
    setUndoCount(undoStack.current.length);
    return last.label;
  };

  const importTransactions = (rows, { account, statement }) => {
    let matched = 0;
    update(`Import ${rows.length} transactions`, (d) => {
      let acc = d.accounts.find((a) => a.id === account.id);
      if (!acc) {
        acc = { id: account.id || uid(), name: account.name, type: account.type, institution: account.institution || "", last4: account.last4 || "", balance: null };
        d.accounts.push(acc);
      }
      if (statement?.closingBalance !== null && statement?.closingBalance !== undefined && (!acc.balanceDate || (statement.period?.end || "") >= acc.balanceDate)) {
        acc.balance = acc.type === "credit" ? -Math.abs(statement.closingBalance) : statement.closingBalance;
        acc.balanceDate = statement.period?.end || null;
      }
      const stId = uid();
      d.statements.push({ id: stId, accountId: acc.id, fileName: statement?.fileName || "", bank: statement?.bank || "", period: statement?.period || null, opening: statement?.openingBalance ?? null, closing: statement?.closingBalance ?? null, reconciliation: statement?.reconciliation || null, skipped: statement?.skipped || 0, importedAt: new Date().toISOString(), count: rows.length, source: statement?.source || "pdf" });
      rows.forEach((r) => d.transactions.push({ id: uid(), accountId: acc.id, statementId: stId, memberId: r.memberId || "m1", scope: "shared", notes: "", excluded: false, business: false, source: statement?.source || "pdf", ...r }));
      d.setupDone = true;
      matched = applyTransferPairs(d, findTransferPairs(d));
    }, [{ action: "import", summary: `Imported ${rows.length} transactions${statement?.fileName ? ` from ${statement.fileName}` : ""}` }]);
    persist(stateRef.current, lock.current).then(() => setSavedAt(new Date()));
    return { matched };
  };

  const matchTransfers = () => {
    const pairs = findTransferPairs(stateRef.current);
    if (pairs.length) update(`Match ${pairs.length} transfer(s)`, (d) => applyTransferPairs(d, pairs), [{ action: "transfer", summary: `Linked ${pairs.length} transfer pair(s) between accounts` }]);
    return pairs.length;
  };

  const moveBill = (ev, to) =>
    update(`Move ${ev.name} to ${to}`, (d) => {
      d.billMoves = (d.billMoves || []).filter((m) => !(m.itemKey === ev.itemKey && m.from === ev.originalDate));
      if (to !== ev.originalDate) d.billMoves.push({ itemKey: ev.itemKey, from: ev.originalDate, to, name: ev.name });
    }, [{ action: "bill", summary: `Moved ${ev.name} (${ev.originalDate}) to ${to}` }]);

  // Change the usual date: every future payment follows
  const moveBillSeries = (ev, to) =>
    update(`Change usual date of ${ev.name}`, (d) => {
      d.billMoves = (d.billMoves || []).filter((m) => m.itemKey !== ev.itemKey);
      const delta = daysBetween(ev.originalDate, to);
      let bill = d.bills.find((b) => b.id === ev.itemKey);
      const anchor = bill?.nextDate || billList(d).suggestions.find((x) => x.key === ev.itemKey)?.nextDate;
      if (!bill) {
        const s = billList(d).suggestions.find((x) => x.key === ev.itemKey);
        if (!s) return;
        bill = { id: uid(), key: s.key, name: s.name, amount: s.amount, frequency: s.frequency, days: s.days || null, category: s.category, isIncome: s.isIncome, confirmed: true, active: true, source: "detected" };
        d.bills.push(bill);
      }
      if (bill.frequency === "semi-monthly" && bill.days?.length === 2) bill.days = bill.days.map((x) => Math.min(31, Math.max(1, x + delta))).sort((a, b) => a - b);
      // Shift the upcoming payment by the same number of days so no payment between today and the new date is lost
      const upcoming = rollForward(anchor || ev.originalDate, bill.frequency, bill.days, today());
      let next = addDays(upcoming, delta);
      let guard = 0;
      // If the new usual date already passed this cycle, keep the still-due payment on its date
      bill.extraDates = next < today() && ev.originalDate !== upcoming ? [upcoming] : [];
      while (next < today() && guard++ < 50) next = nextOccurrence(next, bill.frequency, bill.days);
      bill.nextDate = next;
      bill.usualDateChange = { from: ev.originalDate, to, at: today() };
    }, [{ action: "bill", summary: `Changed usual date of ${ev.name} from ${ev.originalDate} to ${to}` }]);

  // Record a payment or new balance; returns payoff info when the balance reaches zero
  const recordDebtPayment = (id, { payment, newBalance }) => {
    let result = null;
    update("Record debt payment", (d) => {
      const debt = d.debts.find((x) => x.id === id);
      if (!debt) return;
      const before = Number(debt.balance);
      const bal = newBalance !== undefined ? Math.max(0, Number(newBalance)) : Math.max(0, Math.round((before - Number(payment || 0)) * 100) / 100);
      debt.payments = [...(debt.payments || []), { date: today(), amount: payment !== undefined ? Number(payment) : Math.round((before - bal) * 100) / 100, balanceAfter: bal }];
      debt.balance = bal;
      const hits = before > 0 ? crossedMilestones(debt, before, bal) : [];
      if (hits.length) {
        debt.milestones = [...(debt.milestones || []), ...hits];
        debt.milestoneDates = { ...(debt.milestoneDates || {}), ...Object.fromEntries(hits.map((m) => [m, today()])) };
      }
      if (bal === 0 && before > 0) {
        const sv = payoffSavings({ ...debt, balance: before });
        debt.paidOff = { date: today(), ...sv };
        result = { id: debt.id, name: debt.creditor, ...sv };
      } else if (hits.length) result = { id: debt.id, milestone: hits[hits.length - 1], name: debt.creditor, balance: bal };
    }, [{ action: "debt", summary: `Recorded ${payment !== undefined ? `payment of ${payment}` : `new balance ${newBalance}`} for debt` }]);
    return result;
  };

  // Updates saved amount; returns the highest newly reached milestone (25/50/75/100) or null
  const setGoalSaved = (id, current, label = "Update goal savings", changes) => {
    let hit = null;
    update(label, (d) => {
      const g = d.goals.find((x) => x.id === id);
      if (!g) return;
      const before = Number(g.current || 0);
      if (changes) Object.assign(g, changes);
      g.current = Math.max(0, Math.round(Number(current) * 100) / 100);
      const hits = goalMilestones(g, before, g.current);
      if (hits.length) {
        g.milestones = [...(g.milestones || []), ...hits];
        g.milestoneDates = { ...(g.milestoneDates || {}), ...Object.fromEntries(hits.map((m) => [m, today()])) };
        hit = { id: g.id, milestone: hits[hits.length - 1], name: g.name };
      }
    });
    return hit;
  };

  const learnVendor = (store, description) => {
    const key = normStore(store);
    if (!key) return;
    update("Remember receipt store", (d) => {
      const prev = (d.vendorLinks || {})[key];
      d.vendorLinks = { ...(d.vendorLinks || {}), [key]: { merchant: merchantKey(description), label: merchantName(description), count: (prev?.merchant === merchantKey(description) ? prev.count : 0) + 1, lastUsed: today() } };
    });
  };

  const logTrip = (f) =>
    update("Log business miles", (d) => (d.mileage = [...(d.mileage || []), { id: uid(), date: f.date, miles: Number(f.miles), purpose: f.purpose.trim(), from: f.from || "", to: f.to || "", ...(f.txId ? { txId: f.txId } : {}), ...(f.templateId ? { templateId: f.templateId } : {}) }]), [{ action: "mileage", summary: `Logged ${f.miles} business miles (${f.purpose})` }]);

  const unlinkTransfer = (id) =>
    update("Unlink transfer", (d) => {
      const t = d.transactions.find((x) => x.id === id);
      if (!t?.transferPairId) return;
      const pid = t.transferPairId;
      d.transactions.forEach((x) => {
        if (x.transferPairId === pid) {
          delete x.transferPairId;
          x.transferUnlinked = true;
        }
      });
    }, [{ action: "transfer", txId: id, summary: "Unlinked a transfer pair" }]);

  const addTx = (tx) =>
    update("Add transaction", (d) => {
      const res = tx.category ? { category: tx.category } : categorize(tx, d.rules, d.categories);
      d.transactions.push({ id: uid(), memberId: "m1", scope: "shared", notes: "", excluded: false, business: false, source: "manual", flagged: false, ...tx, category: res.category });
      d.setupDone = true;
    }, [{ action: "add", summary: `Added ${tx.description} (${tx.amount})`, after: tx }]);

  const updateTx = (id, patch, { learn = true } = {}) =>
    update("Edit transaction", (d) => {
      const t = d.transactions.find((x) => x.id === id);
      if (!t) return;
      if (patch.category && patch.category !== t.category && learn) {
        d.rules = learnRule(d.rules, t.description, patch.category);
        const key = merchantKey(t.description);
        d.transactions.forEach((o) => {
          if (o.id !== id && o.autoCategory !== false && merchantKey(o.description) === key && !o.splits?.length) {
            o.category = patch.category;
            o.autoCategory = true;
          }
        });
        patch = { ...patch, autoCategory: false, flagged: false, flagReason: "" };
      }
      Object.assign(t, patch);
    }, (prev) => {
      const before = prev.transactions.find((x) => x.id === id);
      return [{ action: "edit", txId: id, summary: `Edited ${before?.description || id}: ${Object.keys(patch).join(", ")}`, before: before ? Object.fromEntries(Object.keys(patch).map((k) => [k, before[k]])) : null, after: patch }];
    });

  const deleteTx = (ids) =>
    update(`Delete ${ids.length} transaction(s)`, (d) => {
      d.transactions = d.transactions.filter((t) => !ids.includes(t.id));
    }, (prev) => prev.transactions.filter((t) => ids.includes(t.id)).map((t) => ({ action: "delete", txId: t.id, summary: `Deleted ${t.description} (${t.amount}) on ${t.date}`, before: t })));

  const addCategory = (name, group, type) =>
    update("Add category", (d) => {
      let id = slug(name);
      if (d.categories.some((c) => c.id === id)) id = `${id}-${uid().slice(0, 4)}`;
      d.categories.push({ id, name, group, type, fixed: false, essential: false, debt: false, custom: true });
    }, [{ action: "category", summary: `Created category ${name}` }]);

  const renameCategory = (id, patch) => update("Edit category", (d) => Object.assign(d.categories.find((c) => c.id === id), patch), [{ action: "category", summary: `Edited category ${id}` }]);

  const mergeCategory = (from, to) =>
    update("Merge categories", (d) => {
      d.transactions.forEach((t) => {
        if (t.category === from) t.category = to;
        t.splits?.forEach((s) => s.category === from && (s.category = to));
      });
      d.rules.forEach((r) => r.category === from && (r.category = to));
      d.categories = d.categories.filter((c) => c.id !== from);
      delete d.budget.limits[from];
    }, [{ action: "category", summary: `Merged ${from} into ${to}` }]);

  const deleteCategory = (id) => mergeCategory(id, "unclassified-transactions");

  const loadSample = () =>
    update("Load sample data", (d) => {
      const s = sampleData();
      d.accounts = s.accounts;
      d.members = s.members;
      d.transactions = s.transactions;
      d.debts = s.debts;
      d.goals = s.goals;
      d.statements = [];
      d.setupDone = true;
      d.isSample = true;
      applyTransferPairs(d, findTransferPairs(d));
    }, [{ action: "sample", summary: "Loaded sample household data" }]);

  const replaceState = (s, label = "Restore backup") => update(label, (d) => Object.assign(d, freshState(), s));

  const inboxMeta = (item) => ({ id: item.id, name: item.name, type: item.type, size: item.size, addedAt: item.addedAt, ...Object.fromEntries(["store", "note", "amount", "date"].filter((k) => item[k]).map((k) => [k, item[k]])) });

  const receiptIds = () => [...(stateRef.current?.transactions || []).flatMap((t) => (t.receipts || []).map((r) => r.id)), ...(stateRef.current?.receiptInbox || []).map((r) => r.id)];

  // Receipt inbox: photos waiting to be matched here, or bundled up and sent to another device
  const addToInbox = async (file, fields = {}, source = "camera") => {
    const item = { id: uid(), name: file.name, type: file.type, size: file.size, addedAt: new Date().toISOString(), source, amount: fields.amount || "", date: fields.date || "", store: fields.store || "", note: fields.note || "" };
    await saveReceipt(item.id, await file.arrayBuffer(), lock.current);
    update("Add to receipt inbox", (d) => (d.receiptInbox = [...(d.receiptInbox || []), item]));
    return item;
  };

  const editInbox = (id, patch) => update("Edit inbox receipt", (d) => Object.assign(d.receiptInbox.find((r) => r.id === id) || {}, patch));

  const attachFromInbox = (id, txId) => {
    const item = (stateRef.current.receiptInbox || []).find((r) => r.id === id);
    const tx = stateRef.current.transactions.find((t) => t.id === txId);
    if (!item || !tx) return;
    update("Attach receipt from inbox", (d) => {
      d.receiptInbox = d.receiptInbox.filter((r) => r.id !== id);
      const t = d.transactions.find((x) => x.id === txId);
      t.receipts = [...(t.receipts || []), inboxMeta(item)];
    }, [{ action: "receipt", txId, summary: `Attached receipt ${item.name} from inbox` }]);
    if (item.store) learnVendor(item.store, tx.description);
  };

  // One undo step for the whole batch
  const autoAttachExact = () => {
    const { pairs, ambiguous } = exactPairs(stateRef.current);
    if (!pairs.length) return { attached: 0, ambiguous };
    update(`Auto-attach ${pairs.length} receipt(s)`, (d) => {
      pairs.forEach(({ itemId, txId }) => {
        const item = d.receiptInbox.find((r) => r.id === itemId);
        const t = d.transactions.find((x) => x.id === txId);
        t.receipts = [...(t.receipts || []), inboxMeta(item)];
        if (item.store) {
          const key = normStore(item.store);
          const prev = (d.vendorLinks || {})[key];
          if (key) d.vendorLinks = { ...(d.vendorLinks || {}), [key]: { merchant: merchantKey(t.description), label: merchantName(t.description), count: (prev?.merchant === merchantKey(t.description) ? prev.count : 0) + 1, lastUsed: today() } };
        }
      });
      const ids = new Set(pairs.map((p) => p.itemId));
      d.receiptInbox = d.receiptInbox.filter((r) => !ids.has(r.id));
    }, pairs.map(({ txId }) => ({ action: "receipt", txId, summary: "Auto-attached exact-match receipt from inbox" })));
    return { attached: pairs.length, ambiguous };
  };

  const applySync = (incoming, mode) => {
    const next = mode === "replace" ? replaceSync(stateRef.current, incoming) : mergeSync(stateRef.current, incoming);
    update(mode === "replace" ? "Replace with synced data" : "Merge synced data", (d) => Object.assign(d, next), [{ action: "sync", summary: `${mode === "replace" ? "Replaced data with" : "Merged"} data from another device (${(incoming.transactions || []).length} transactions)` }]);
    persist(stateRef.current, lock.current).then(() => setSavedAt(new Date()));
  };

  const removeFromInbox = async (ids) => {
    update("Remove from receipt inbox", (d) => (d.receiptInbox = (d.receiptInbox || []).filter((r) => !ids.includes(r.id))));
    undoStack.current = undoStack.current.filter((u) => !(u.state.receiptInbox || []).some((r) => ids.includes(r.id)));
    setUndoCount(undoStack.current.length);
    await Promise.all(ids.map((id) => deleteReceipt(id)));
  };

  const exportInboxBundle = async (ids, password) => {
    const items = [];
    for (const r of (stateRef.current.receiptInbox || []).filter((x) => ids.includes(x.id))) {
      const buf = await loadReceipt(r.id, lock.current);
      if (buf) items.push({ ...r, data: b64(buf) });
    }
    return { text: await sealJSON(BUNDLE, { items, createdAt: new Date().toISOString() }, password), count: items.length };
  };

  const importInboxBundle = async (text, password) => {
    const { items = [] } = await openJSON(BUNDLE, text, password);
    const known = new Set(receiptIds());
    const fresh = items.filter((r) => !known.has(r.id));
    for (const r of fresh) await saveReceipt(r.id, unb64(r.data), lock.current);
    if (fresh.length) update("Import phone receipts", (d) => (d.receiptInbox = [...(d.receiptInbox || []), ...fresh.map(({ data, ...meta }) => ({ ...meta, source: "bundle" }))]));
    return { added: fresh.length, skipped: items.length - fresh.length };
  };

  // Photos added after `since` (attached + inbox), sealed with the sync code
  const exportSyncPhotos = async (since, code) => {
    const s = stateRef.current;
    const metas = [...s.transactions.flatMap((t) => t.receipts || []).map((m) => ({ ...m, inbox: false })), ...(s.receiptInbox || []).map((m) => ({ ...m, inbox: true }))].filter((m) => !since || String(m.addedAt) > since);
    const items = [];
    for (const m of metas) {
      const buf = await loadReceipt(m.id, lock.current).catch(() => null);
      if (buf) items.push({ ...m, data: b64(buf) });
    }
    return { text: items.length ? await sealJSON(PHOTO_FORMAT, { items, createdAt: new Date().toISOString() }, cleanCode(code)) : null, count: items.length };
  };

  const importSyncPhotos = async (text, code) => {
    let obj;
    try { obj = JSON.parse(text); } catch { throw new Error("This isn't a photos file from Sync devices."); }
    if (obj.format !== PHOTO_FORMAT) throw new Error("This isn't a photos file from Sync devices.");
    let items;
    try { ({ items = [] } = await openJSON(PHOTO_FORMAT, text, cleanCode(code))); } catch { throw new Error("Wrong sync code for this photos file."); }
    const s = stateRef.current;
    const linked = new Set(s.transactions.flatMap((t) => (t.receipts || []).map((r) => r.id)));
    const inInbox = new Set((s.receiptInbox || []).map((r) => r.id));
    let added = 0, waiting = 0, already = 0;
    const toInbox = [];
    for (const { data, inbox, ...meta } of items) {
      const usable = linked.has(meta.id) || inInbox.has(meta.id) || (inbox && !linked.has(meta.id));
      if (!usable) { waiting++; continue; }
      if (await loadReceipt(meta.id, lock.current).catch(() => null)) { already++; continue; }
      await saveReceipt(meta.id, unb64(data), lock.current);
      if (inbox && !inInbox.has(meta.id) && !linked.has(meta.id)) toInbox.push({ ...meta, source: "sync" });
      added++;
    }
    if (toInbox.length) update("Add synced inbox photos", (d) => (d.receiptInbox = [...(d.receiptInbox || []), ...toInbox]));
    return { added, waiting, already };
  };

  const addReceipt = async (txId, file) => {
    const meta = { id: uid(), name: file.name, type: file.type, size: file.size, addedAt: new Date().toISOString() };
    await saveReceipt(meta.id, await file.arrayBuffer(), lock.current);
    update("Attach receipt", (d) => {
      const t = d.transactions.find((x) => x.id === txId);
      if (t) t.receipts = [...(t.receipts || []), meta];
    }, [{ action: "receipt", txId, summary: `Attached receipt ${file.name}` }]);
    return meta;
  };

  const getReceipt = async (meta) => {
    const buf = await loadReceipt(meta.id, lock.current);
    return buf ? new Blob([buf], { type: meta.type || "application/octet-stream" }) : null;
  };

  // File is kept until no undo history references it; removing only detaches it
  const removeReceipt = (txId, rid) =>
    update("Remove receipt", (d) => {
      const t = d.transactions.find((x) => x.id === txId);
      if (t) t.receipts = (t.receipts || []).filter((r) => r.id !== rid);
    }, [{ action: "receipt", txId, summary: "Removed a receipt" }]);

  const purgeReceipt = async (txId, rid) => {
    removeReceipt(txId, rid);
    undoStack.current = undoStack.current.filter((u) => !u.state.transactions.some((t) => t.receipts?.some((r) => r.id === rid)));
    setUndoCount(undoStack.current.length);
    await deleteReceipt(rid);
  };

  const exportReceipts = async () => {
    const out = [];
    for (const id of [...new Set(receiptIds())]) {
      const buf = await loadReceipt(id, lock.current).catch(() => null);
      if (buf) out.push({ id, data: b64(buf) });
    }
    return out;
  };

  const restoreBackup = async ({ state: s, receipts }) => {
    for (const r of receipts || []) await saveReceipt(r.id, unb64(r.data), lock.current);
    replaceState(s);
    return (receipts || []).length;
  };

  const setPasscode = async (pass) => {
    const old = lock.current;
    await disableBio();
    lock.current = pass ? await makeLock(pass) : null;
    await reencryptReceipts(receiptIds(), old, lock.current);
    await persist(stateRef.current, lock.current);
    setSavedAt(new Date());
  };

  const wipe = async () => {
    await wipeAll();
    lock.current = null;
    undoStack.current = [];
    setUndoCount(0);
    setState(freshState());
  };

  const isDuplicate = (accountId) => {
    const keys = new Set((stateRef.current?.transactions || []).filter((t) => t.accountId === accountId).map(txKey));
    return (t) => keys.has(txKey({ ...t, accountId }));
  };

  const value = { state, status, savedAt, undoCount, update, undo, unlock, importTransactions, matchTransfers, unlinkTransfer, moveBill, moveBillSeries, recordDebtPayment, addReceipt, getReceipt, purgeReceipt, exportReceipts, logTrip, learnVendor, setGoalSaved, addToInbox, editInbox, attachFromInbox, autoAttachExact, applySync, exportSyncPhotos, importSyncPhotos, removeFromInbox, exportInboxBundle, importInboxBundle, restoreBackup, addTx, updateTx, deleteTx, addCategory, renameCategory, mergeCategory, deleteCategory, loadSample, replaceState, setPasscode, wipe, isDuplicate, hasPasscode: () => !!lock.current, lockNow, unlockWithBio, turnOnBio, disableBio };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
